import JSZip from "jszip";
import { buildRulebookIllustrationFile } from "./rulebookImportUtils";

const DESIGNER_JSON_PATH = "designer.json";
const PROTOTYPE_JSON_PATTERN = /^prototypes\/[^/]+\/prototype\.json$/;

/**
 * @param {unknown} value
 * @returns {string|undefined}
 */
const optionalString = (value) => {
  if (value === undefined || value === null) {
    return undefined;
  }
  return String(value);
};

/**
 * @param {string} filename
 * @returns {string|undefined}
 */
const guessFileMimeType = (filename) => {
  const lower = filename.toLowerCase();

  if (lower.endsWith(".png")) {
    return "image/png";
  }
  if (lower.endsWith(".jpg") || lower.endsWith(".jpeg")) {
    return "image/jpeg";
  }
  if (lower.endsWith(".pdf")) {
    return "application/pdf";
  }

  return undefined;
};

/**
 * @param {JSZip} zip
 * @param {string} folderPath
 * @param {string} filename
 * @returns {Promise<File|undefined>}
 */
const resolveZipFile = async (zip, folderPath, filename) => {
  const zipEntry = zip.file(`${folderPath}/${filename}`);

  if (!zipEntry || zipEntry.dir) {
    return undefined;
  }

  const blob = await zipEntry.async("blob");
  const mimeType = guessFileMimeType(filename) || blob.type || "application/octet-stream";

  return new File([blob], filename, { type: mimeType });
};

/**
 * @param {unknown} value
 * @returns {number|undefined}
 */
const optionalNumber = (value) => {
  if (value === undefined || value === null || value === "") {
    return undefined;
  }

  const number = Number(value);
  return Number.isFinite(number) ? number : undefined;
};

/**
 * @param {object} designer
 * @param {JSZip} zip
 * @returns {Promise<NestiFyzDesignerProfile>}
 */
const parseDesigner = async (designer, zip) => {
  /** @type {NestiFyzDesignerProfile} */
  const profile = {};
  const name = optionalString(designer.name);
  const location = optionalString(designer.location);
  const avatarFilename = optionalString(designer.avatar);

  if (name !== undefined) {
    profile.name = name;
  }
  if (location !== undefined) {
    profile.location = location;
  }

  if (avatarFilename) {
    const avatarZipEntry = zip.file(avatarFilename);

    if (avatarZipEntry && !avatarZipEntry.dir) {
      const blob = await avatarZipEntry.async("blob");
      const mimeType = guessFileMimeType(avatarFilename) || blob.type || "application/octet-stream";

      profile.avatarFile = new File([blob], avatarFilename, { type: mimeType });
    }
  }

  return profile;
};

/**
 * @param {object} prototype
 * @param {JSZip} zip
 * @param {string} folderPath
 * @returns {Promise<NestiFyzGame>}
 */
const parsePrototype = async (prototype, zip, folderPath) => {
  /** @type {NestiFyzGame} */
  const game = {};

  const stringFields = [
    "id",
    "createdAt",
    "updatedAt",
    "title",
    "status",
    "visibility",
    "shortDescription",
    "longDescription",
    "complexity",
    "languageDependency",
    "pitchVideoLink",
    "playableDigitalVersionLink",
    "uniqueSellingPoint1Title",
    "uniqueSellingPoint1",
    "uniqueSellingPoint2Title",
    "uniqueSellingPoint2",
    "uniqueSellingPoint3Title",
    "uniqueSellingPoint3",
    "components",
    "estimatedCosts",
    "inspiredBy",
  ];

  for (const field of stringFields) {
    const value = optionalString(prototype[field]);
    if (value !== undefined) {
      game[field] = value;
    }
  }

  const numberFields = ["minPlayers", "maxPlayers", "minTime", "maxTime", "minAge"];
  for (const field of numberFields) {
    const value = optionalNumber(prototype[field]);
    if (value !== undefined) {
      game[field] = value;
    }
  }

  if (Array.isArray(prototype.tags)) {
    game.tags = prototype.tags.map((tag) => String(tag));
  }

  if (Array.isArray(prototype.scoreBasedAttributes)) {
    game.scoreBasedAttributes = prototype.scoreBasedAttributes;
  }

  if (Array.isArray(prototype.progressChecklist)) {
    game.progressChecklist = prototype.progressChecklist;
  }

  if (Array.isArray(prototype.rulebooks)) {
    const rulebooks = [];

    for (const rulebook of prototype.rulebooks) {
      if (!rulebook || typeof rulebook !== "object") {
        continue;
      }

      const document = optionalString(rulebook.document);
      const documentFile = document ? await resolveZipFile(zip, folderPath, document) : undefined;
      const contentFields = [
        "introduction",
        "goal",
        "setup",
        "gameplay",
        "endOfGame",
        "extraRules",
        "appendix",
        "faq",
        "credits",
        "changelog",
      ];
      /** @type {Record<string, string|undefined>} */
      const content = {};

      for (const field of contentFields) {
        const value = optionalString(rulebook[field]);
        if (value !== undefined) {
          content[field] = value;
        }
      }

      /** @type {import("./parseExport").NestiFyzRulebook} */
      const parsedRulebook = {
        ...rulebook,
        ...content,
        document,
        versionName: optionalString(rulebook.versionName),
        createdAt: optionalString(rulebook.createdAt),
        updatedAt: optionalString(rulebook.updatedAt),
        documentFile,
      };

      const illustrationFiles = [];

      if (Array.isArray(rulebook.media)) {
        for (let index = 0; index < rulebook.media.length; index++) {
          const mediaFilename = optionalString(rulebook.media[index]);
          if (!mediaFilename) {
            continue;
          }

          const mediaFile = await resolveZipFile(zip, folderPath, mediaFilename);
          if (mediaFile) {
            illustrationFiles.push(buildRulebookIllustrationFile(mediaFile, parsedRulebook, index));
          }
        }
      }

      rulebooks.push({
        ...parsedRulebook,
        illustrationFiles: illustrationFiles.length > 0 ? illustrationFiles : undefined,
      });
    }

    game.rulebooks = rulebooks;
  }

  if (Array.isArray(prototype.playtests)) {
    const playtests = [];

    for (const playtest of prototype.playtests) {
      if (!playtest || typeof playtest !== "object") {
        continue;
      }

      const mediaFiles = [];

      if (Array.isArray(playtest.media)) {
        for (const mediaFilename of playtest.media) {
          const filename = optionalString(mediaFilename);
          if (!filename) {
            continue;
          }

          const mediaFile = await resolveZipFile(zip, folderPath, filename);
          if (mediaFile) {
            mediaFiles.push(mediaFile);
          }
        }
      }

      playtests.push({
        ...playtest,
        mediaFiles: mediaFiles.length > 0 ? mediaFiles : undefined,
      });
    }

    game.playtests = playtests;
  }

  const coverFilename = optionalString(prototype.avatar);
  if (coverFilename) {
    const coverFile = await resolveZipFile(zip, folderPath, coverFilename);
    if (coverFile) {
      game.coverFile = coverFile;
    }
  }

  if (Array.isArray(prototype.media)) {
    const mediaFiles = [];

    for (const mediaFilename of prototype.media) {
      const filename = optionalString(mediaFilename);
      if (!filename) {
        continue;
      }

      const mediaFile = await resolveZipFile(zip, folderPath, filename);
      if (mediaFile) {
        mediaFiles.push(mediaFile);
      }
    }

    if (mediaFiles.length > 0) {
      game.mediaFiles = mediaFiles;
    }
  }

  return game;
};

/**
 * @typedef {object} NestiFyzDesignerProfile
 * @property {string} [name]
 * @property {string} [location]
 * @property {File} [avatarFile]
 */

/**
 * @typedef {object} NestiFyzScoreBasedAttribute
 * @property {string} [attributeName]
 * @property {number} [attributeValue]
 */

/**
 * @typedef {object} NestiFyzProgressChecklistItem
 * @property {string} [item]
 * @property {boolean} [value]
 */

/**
 * @typedef {object} NestiFyzRulebook
 * @property {string} [id]
 * @property {string} [createdAt]
 * @property {string} [updatedAt]
 * @property {string} [versionName]
 * @property {string} [document]
 * @property {File} [documentFile]
 * @property {string} [introduction]
 * @property {string} [goal]
 * @property {string} [setup]
 * @property {string} [gameplay]
 * @property {string} [endOfGame]
 * @property {string} [extraRules]
 * @property {string} [appendix]
 * @property {string} [faq]
 * @property {string} [credits]
 * @property {string} [changelog]
 * @property {string[]} [media]
 * @property {File[]} [illustrationFiles]
 */

/**
 * @typedef {object} NestiFyzPlaytestParticipant
 * @property {string} [name]
 * @property {string} [email]
 * @property {number} [score]
 * @property {boolean} [isWinner]
 */

/**
 * @typedef {object} NestiFyzPlaytestFeedbackResponse
 * @property {string} [question]
 * @property {string} [answer]
 */

/**
 * @typedef {object} NestiFyzPlaytestFeedback
 * @property {string} [status]
 * @property {string} [email]
 * @property {string} [playtesterName]
 * @property {NestiFyzPlaytestFeedbackResponse[]} [responses]
 */

/**
 * @typedef {object} NestiFyzPlayTest
 * @property {string} [id]
 * @property {string} [createdAt]
 * @property {string} [updatedAt]
 * @property {string} [rulebookId]
 * @property {string} [playtestName] // ignored
 * @property {string} [playtestType] // medium ("In-person"|"Online") or legacy audience labels
 * @property {string} [guidedOrUnguided] // audience labels e.g. "Blind (No Help/Explanation)"
 * @property {string} [playthroughType] // "Full"|"Partial", or legacy "Solo" / "In Person"
 * @property {NestiFyzPlaytestParticipant[]} [participants]
 * @property {string} [playtestDate]
 * @property {string} [setupStartTime]
 * @property {string} [setupEndTime]
 * @property {string} [rulesExplanationStartTime]
 * @property {string} [rulesExplanationEndTime]
 * @property {string} [playtestStartTime]
 * @property {string} [playtestEndTime]
 * @property {string[]} [notes]
 * @property {string[]} [ideas]
 * @property {string[]} [observations]
 * @property {string[]} [media]
 * @property {File[]} [mediaFiles]
 * @property {NestiFyzPlaytestFeedback[]} [feedbacks]
 */

/**
 * @typedef {object} NestiFyzGame
 * @property {string} [id]
 * @property {string} [createdAt]
 * @property {string} [title]
 * @property {string} [status]
 * @property {string} [shortDescription]
 * @property {string} [longDescription]
 * @property {string[]} [tags]
 * @property {number} [minPlayers]
 * @property {number} [maxPlayers]
 * @property {number} [minTime]
 * @property {number} [maxTime]
 * @property {number} [minAge]
 * @property {string} [pitchVideoLink]
 * @property {string} [playableDigitalVersionLink]
 * @property {string} [updatedAt] // ignore
 * @property {string} [visibility] // ignore
 * @property {string} [complexity]
 * @property {string} [languageDependency]
 * @property {NestiFyzScoreBasedAttribute[]} [scoreBasedAttributes]
 * @property {string} [uniqueSellingPoint1Title]
 * @property {string} [uniqueSellingPoint1]
 * @property {string} [uniqueSellingPoint2Title]
 * @property {string} [uniqueSellingPoint2]
 * @property {string} [uniqueSellingPoint3Title]
 * @property {string} [uniqueSellingPoint3]
 * @property {string} [components]
 * @property {string} [estimatedCosts]
 * @property {string} [inspiredBy]
 * @property {File[]} [mediaFiles]
 * @property {NestiFyzProgressChecklistItem[]} [progressChecklist]
 * @property {NestiFyzRulebook[]} [rulebooks]
 * @property {NestiFyzPlayTest[]} [playtests]
 * @property {File} [coverFile]
 */

/**
 * @typedef {object} NestiFyzExport
 * @property {NestiFyzDesignerProfile} profile
 * @property {NestiFyzGame[]} games
 */

/**
 * @param {ArrayBuffer} zipData
 * @returns {Promise<NestiFyzExport>}
 */
export const parseNestiFyzExport = async (zipData) => {
  const zip = await JSZip.loadAsync(zipData);

  const designerFile = zip.file(DESIGNER_JSON_PATH);
  if (!designerFile) {
    throw new Error("Missing designer.json");
  }

  const designerJson = JSON.parse(await designerFile.async("text"));
  const profile = await parseDesigner(designerJson, zip);

  const games = [];
  const prototypePaths = Object.keys(zip.files).filter(
    (path) => PROTOTYPE_JSON_PATTERN.test(path) && !zip.files[path].dir
  );

  for (const path of prototypePaths) {
    const file = zip.file(path);
    if (!file) {
      continue;
    }

    const prototypeJson = JSON.parse(await file.async("text"));
    const folderPath = path.replace(/\/prototype\.json$/, "");
    games.push(await parsePrototype(prototypeJson, zip, folderPath));
  }

  return { profile, games };
};
